import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../../services/auth';
import { TokenManager } from '../../services/api';

// 异步登录action
export const loginAsync = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error?.response?.data?.message || error?.message || '登录失败',
        data: error?.data,
        code: error?.code,
      });
    }
  }
);

// 异步注册action
export const registerAsync = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authService.register(userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || '注册失败');
    }
  }
);

// 异步获取用户信息action
export const getUserInfoAsync = createAsyncThunk(
  'auth/getUserInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authService.getUserInfo();
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message || '获取用户信息失败');
    }
  }
);

// 异步退出登录action
export const logoutAsync = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      return true;
    } catch (error) {
      // 即使后端调用失败，也要清除本地状态
      console.error('退出登录接口调用失败:', error);
      return true;
    }
  }
);

const initialState = {
  isAuthenticated: false,
  user: null,
  token: null,
  permissions: [],
  roles: [],
  initialized: false,
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.permissions = [];
      state.roles = [];
      state.error = null;
      // 清除 Token（同时清除 sessionStorage 和 localStorage）
      TokenManager.clearToken();
    },
    clearError: (state) => {
      state.error = null;
    },
    setPermissions: (state, action) => {
      state.permissions = action.payload;
    },
    setRoles: (state, action) => {
      state.roles = action.payload;
    },
    updateUserPermissions: (state, action) => {
      state.permissions = action.payload.permissions || [];
      state.roles = action.payload.roles || [];
    },
    // 从 storage 恢复 token（用于应用初始化）
    restoreToken: (state) => {
      const token = TokenManager.getToken();
      if (token) {
        state.token = token;
        state.isAuthenticated = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // 登录
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token || null;
        state.permissions = action.payload.permissions || [];
        state.roles = action.payload.roles || [];
        state.error = null;
        // 使用 TokenManager 保存 token（同时设置到 sessionStorage 和 localStorage）
        if (action.payload.token) {
          TokenManager.setToken(action.payload.token);
        }
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.error = action.payload;
      })
      // 注册
      .addCase(registerAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerAsync.fulfilled, (state) => {
        state.loading = false;
        state.initialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.roles = [];
        state.error = null;
      })
      .addCase(registerAsync.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.error = action.payload;
      })
      // 获取用户信息
      .addCase(getUserInfoAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserInfoAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.isAuthenticated = true;
        state.user = action.payload.user || action.payload;
        state.permissions = action.payload.permissions || [];
        state.roles = action.payload.roles || [];
      })
      .addCase(getUserInfoAsync.rejected, (state, action) => {
        state.loading = false;
        state.initialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.roles = [];
        state.error = action.payload;
      })
      // 退出登录
      .addCase(logoutAsync.fulfilled, (state) => {
        state.initialized = true;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.permissions = [];
        state.roles = [];
        state.error = null;
      });
  },
});

export const { logout, clearError, setPermissions, setRoles, updateUserPermissions, restoreToken } = authSlice.actions;
export default authSlice.reducer;
